import type { Opening, Wall } from '../hp/types';

type Point3 = { x: number; y: number; z: number };

/**
 * Where a ray enters the box an opening cuts through its wall: along the wall its width about its
 * centre, up from its sill, across the wall's whole thickness (plus `margin` either side). A window's
 * panes and an open doorway are air, so a ray aimed at them hits whatever stands behind — the floor of
 * the room, the far wall, the neighbour's twin wall. The picker asks this box first and lets the
 * opening win unless something stands in front of it.
 *
 * `top` clips the box from above: the cutaway and walls-down cut a wall and what hangs on it, and the
 * part of a window that is not drawn cannot be clicked. Returns the distance along the ray (0 when
 * the ray starts inside the box), or null when it misses.
 */
export function openingRayEntry(ray: { origin: Point3; direction: Point3 }, wall: Pick<Wall, 'start' | 'dir' | 'thickness'>,
  opening: Pick<Opening, 'offset' | 'width' | 'height' | 'sill'>, top = Infinity, margin = .01): number | null {
  const high = Math.min(opening.sill + opening.height, top);
  if (!(high > opening.sill) || !(opening.width > 0)) return null;
  // The wall's own frame: along its direction from `start`, up, and across from its centre line.
  const u = { x: wall.dir.x, z: wall.dir.z }, w = { x: -wall.dir.z, z: wall.dir.x };
  const ox = ray.origin.x - wall.start.x, oz = ray.origin.z - wall.start.z;
  const axes: Array<[origin: number, direction: number, low: number, high: number]> = [
    [ox * u.x + oz * u.z, ray.direction.x * u.x + ray.direction.z * u.z, opening.offset - opening.width / 2, opening.offset + opening.width / 2],
    [ray.origin.y, ray.direction.y, opening.sill, high],
    [ox * w.x + oz * w.z, ray.direction.x * w.x + ray.direction.z * w.z, -wall.thickness / 2 - margin, wall.thickness / 2 + margin],
  ];
  let enter = -Infinity, exit = Infinity;
  for (const [origin, direction, low, highBound] of axes) {
    if (Math.abs(direction) < 1e-12) {
      if (origin < low || origin > highBound) return null;
      continue;
    }
    const a = (low - origin) / direction, b = (highBound - origin) / direction;
    enter = Math.max(enter, Math.min(a, b)); exit = Math.min(exit, Math.max(a, b));
    if (exit < enter) return null;
  }
  if (exit < 0) return null;
  return Math.max(enter, 0);
}
